import {
  ArrowUpRight,
  Check,
  Video,
  ClipboardCheck,
  BookOpenCheck,
  LineChart,
  FolderOpen,
  Sparkles,
  Users,
  User,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useSite } from '../lib/SiteContentContext';
import { BOOK_TRIAL_URL } from '../lib/config';

const ICONS = {
  Video,
  ClipboardCheck,
  BookOpenCheck,
  LineChart,
  FolderOpen,
  Sparkles,
};

function CourseComparisonCard({ course }) {
  const isGCSE = course.level === 'GCSE';
  return (
    <article
      className="relative flex flex-col rounded-3xl bg-white border border-brand-ink/10 shadow-sm hover:shadow-lg transition-shadow p-6 md:p-7"
      data-testid={`course-card-${course.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-semibold uppercase tracking-widest bg-brand-sun text-brand-ink px-2.5 py-1 rounded-full">
          {course.level}
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-widest text-brand-ink/60">
          {course.yearGroup}
        </span>
      </div>

      <h3 className="mt-3 font-display text-2xl md:text-3xl text-brand-ink leading-tight">
        {course.title}
      </h3>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {course.boards.map((b) => (
          <span
            key={b}
            className="text-[11px] font-semibold uppercase tracking-widest bg-brand-mint/60 text-brand-ink px-2 py-1 rounded-full"
          >
            {b}
          </span>
        ))}
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest bg-brand-sun/70 text-brand-ink px-2 py-1 rounded-full">
          <Users className="w-3 h-3" aria-hidden="true" /> Small groups
        </span>
      </div>

      {isGCSE && (
        <p className="mt-3 text-sm text-brand-ink/70 font-medium">
          Biology · Chemistry · Physics
        </p>
      )}

      {/* Pricing block */}
      <div className="mt-5 pt-5 border-t border-brand-ink/10">
        <div className="font-display text-2xl md:text-3xl text-brand-ink" data-testid={`course-price-${course.id}`}>
          {course.price.headline}
        </div>
        {course.price.subline && (
          <div className="mt-1 text-sm text-brand-ink/70">{course.price.subline}</div>
        )}
      </div>

      {/* Highlights */}
      <ul className="mt-5 space-y-2.5">
        {course.highlights.map((h) => (
          <li key={h} className="flex items-start gap-2.5 text-sm text-brand-ink">
            <span className="mt-0.5 inline-flex w-5 h-5 items-center justify-center rounded-full bg-brand-sun/90 text-brand-ink shrink-0">
              <Check className="w-3.5 h-3.5" aria-hidden="true" />
            </span>
            <span>{h}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function OneToOneCard() {
  const highlights = [
    'Individual lessons with a qualified teacher',
    'Teaching tailored to your goals and learning pace',
    'Targeted support with difficult topics',
    'Clear, step-by-step explanations',
    'Exam-board-specific practice and exam technique',
    'Personalised homework and detailed feedback',
    'Access to Science Sorted flashcards',
    'Regular progress checks',
  ];
  return (
    <article
      className="relative flex flex-col rounded-3xl bg-white border border-brand-ink/10 shadow-sm hover:shadow-lg transition-shadow p-6 md:p-7"
      data-testid="course-card-one-to-one"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-semibold uppercase tracking-widest bg-brand-sun text-brand-ink px-2.5 py-1 rounded-full">
          GCSE &amp; A-Level
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-widest text-brand-ink/60">
          Years 9&ndash;13
        </span>
      </div>

      <h3 className="mt-3 font-display text-2xl md:text-3xl text-brand-ink leading-tight">
        1:1 Science Tuition
      </h3>

      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-widest bg-brand-mint/60 text-brand-ink px-2 py-1 rounded-full">
          AQA
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-widest bg-brand-mint/60 text-brand-ink px-2 py-1 rounded-full">
          OCR
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest bg-brand-sun/70 text-brand-ink px-2 py-1 rounded-full">
          <User className="w-3 h-3" aria-hidden="true" /> One-to-one
        </span>
      </div>

      <p className="mt-3 text-sm text-brand-ink/70 font-medium">
        Personalised teaching. Your pace. Your goals.
      </p>

      {/* Pricing block */}
      <div className="mt-5 pt-5 border-t border-brand-ink/10">
        <div className="font-display text-2xl md:text-3xl text-brand-ink">
          Enquire for pricing
        </div>
        <div className="mt-1 text-sm text-brand-ink/70">
          Dedicated online tuition tailored to your individual needs.
        </div>
      </div>

      {/* Highlights */}
      <ul className="mt-5 space-y-2.5">
        {highlights.map((h) => (
          <li key={h} className="flex items-start gap-2.5 text-sm text-brand-ink">
            <span className="mt-0.5 inline-flex w-5 h-5 items-center justify-center rounded-full bg-brand-sun/90 text-brand-ink shrink-0">
              <Check className="w-3.5 h-3.5" aria-hidden="true" />
            </span>
            <span>{h}</span>
          </li>
        ))}
      </ul>

      {/* Flex spacer pushes the CTA to the bottom so cards align visually */}
      <div className="flex-grow" aria-hidden="true" />

      <Button
        asChild
        className="mt-6 bg-brand-sun text-brand-ink hover:bg-brand-ink hover:text-brand-sun rounded-full h-12 font-semibold shadow-sm transition-colors"
      >
        <Link
          to="/contact?interest=1-to-1"
          className="gap-2"
          data-testid="one-to-one-enquire-cta"
        >
          Enquire about 1:1 tuition
          <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </Button>
    </article>
  );
}

function IncludedItem({ item }) {
  const Icon = ICONS[item.icon] || Sparkles;
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-10 h-10 rounded-xl bg-brand-sun/90 grid place-items-center">
        <Icon className="w-5 h-5 text-brand-ink" aria-hidden="true" />
      </div>
      <div>
        <div className="font-display text-lg text-brand-ink leading-tight">{item.title}</div>
        <p className="mt-1 text-sm text-brand-ink/70 leading-relaxed">{item.body}</p>
      </div>
    </div>
  );
}

export default function Courses() {
  const { courses, courseIncludes } = useSite();
  return (
    <main>
      {/* Compact intro */}
      <section className="pt-10 md:pt-14 pb-6 md:pb-8 bg-white">
        <div className="container max-w-4xl">
          <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">
            Our courses
          </div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl md:text-6xl text-brand-ink leading-tight">
            Find the right <span className="italic text-brand-teal">science course</span>.
          </h1>
          <p className="mt-3 text-brand-ink/70 text-base md:text-lg max-w-2xl">
            Live, one-hour GCSE and A Level lessons for AQA and OCR students.
          </p>
        </div>
      </section>

      {/* Comparison cards */}
      <section className="pb-10 md:pb-14 bg-white" aria-labelledby="compare-heading">
        <h2 id="compare-heading" className="sr-only">
          Compare Science Sorted courses
        </h2>
        <div className="container">
          <div className="grid md:grid-cols-3 gap-5 md:gap-6 items-stretch">
            {courses.map((c) => (
              <CourseComparisonCard key={c.id} course={c} />
            ))}
            <OneToOneCard />
          </div>
          <p className="mt-6 text-sm md:text-base text-brand-ink/75">
            <span className="font-semibold text-brand-ink">Your first lesson is free.</span> No obligation to continue.
          </p>
        </div>
      </section>

      {/* What's included */}
      <section className="py-12 md:py-16 bg-brand-cream" aria-labelledby="included-heading">
        <div className="container">
          <div className="max-w-3xl">
            <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">
              What you get
            </div>
            <h2
              id="included-heading"
              className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink"
            >
              What&rsquo;s included in every Science Sorted course?
            </h2>
          </div>
          <div className="mt-8 md:mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {courseIncludes.map((item) => (
              <IncludedItem key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* Slim CTA strip */}
      <section className="py-10 md:py-14" aria-labelledby="courses-cta-heading">
        <div className="container">
          <div className="rounded-3xl bg-brand-sun px-6 md:px-10 py-6 md:py-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 md:justify-between">
            <div>
              <h3
                id="courses-cta-heading"
                className="font-display text-xl md:text-2xl text-brand-ink leading-tight"
              >
                Not sure which course is right?
              </h3>
              <p className="mt-1 text-brand-ink/80 text-sm md:text-base">
                Book a free one-hour trial lesson and we&rsquo;ll help you find the right class.
              </p>
            </div>
            <Button
              asChild
              size="lg"
              className="bg-brand-ink text-brand-cream hover:bg-brand-teal rounded-full h-12 px-6 shrink-0"
            >
              <a
                href={BOOK_TRIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="gap-2"
                data-testid="courses-book-trial-cta"
              >
                Book a free trial <ArrowUpRight className="w-4 h-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
